import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import AdBanner from '../components/AdBanner';
import { AD_SLOTS } from '../config/adSlots';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useStructuredData } from '../hooks/useStructuredData';
import {
  tournamentApi,
  TournamentDetails,
  Wrestler,
  WrestlerMatchResults,
  MatchResult,
} from '../services/api-client';
import { MapPinIcon, CalendarIcon, ScaleIcon } from '../components/icons';
import { formatDateRange } from '../utils/dateTime';

function statusLabel(status: string | null): { text: string; className: string } {
  switch (status) {
    case 'upcoming':
      return { text: 'Upcoming', className: 'bg-blue-100 text-blue-800' };
    case 'in_progress':
    case 'live':
      return { text: 'Live', className: 'bg-green-100 text-green-800' };
    case 'completed':
      return { text: 'Completed', className: 'bg-gray-100 text-gray-700' };
    default:
      return { text: 'Upcoming', className: 'bg-blue-100 text-blue-800' };
  }
}

const WIN_TYPE_LEGEND: { type: string; description: string; bonus: string }[] = [
  { type: 'Decision', description: 'Win by 1-7 points', bonus: 'No bonus' },
  {
    type: 'Sudden Victory / Tie Breaker',
    description: 'Overtime win after a tied match',
    bonus: 'No bonus',
  },
  { type: 'Major Decision', description: 'Win by 8-14 points', bonus: '+1.0 bonus' },
  { type: 'Tech Fall', description: 'Win by 15+ points (match stopped)', bonus: '+1.5 bonus' },
  {
    type: 'Fall (Pin)',
    description: "Win by pinning opponent's shoulders to the mat",
    bonus: '+2.0 bonus',
  },
  {
    type: 'Forfeit / Default / DQ',
    description: 'Win without completing the match',
    bonus: '+2.0 bonus',
  },
];

const SCORING_RULES = [
  { label: 'Championship bracket win', value: '1.0 pt advancement + bonus' },
  { label: 'Consolation bracket win', value: '0.5 pt advancement + bonus' },
  { label: 'Bye + win (championship)', value: '1.0 pt advancement (for the bye)' },
  { label: 'Bye + win (consolation)', value: '0.5 pt advancement (for the bye)' },
  { label: 'Placement match win', value: 'Bonus only (no advancement)' },
];

function ScoringBreakdown({
  matchResults,
  teamPoints,
  place,
}: {
  matchResults: WrestlerMatchResults;
  teamPoints: number;
  place: number | null;
}) {
  const [showLegend, setShowLegend] = useState(false);

  return (
    <div className="px-4 py-4 bg-gray-50 border-t border-gray-200">
      {/* Points summary */}
      <div className="flex gap-6 mb-4 text-sm">
        <div className="bg-white rounded-lg px-4 py-2.5 border border-gray-200">
          <div className="text-xs text-gray-500 uppercase tracking-wide">
            Placing{place ? ` (${ordinal(place)})` : ''}
          </div>
          <div className="text-lg font-bold text-purple-700">{matchResults.placingPoints}</div>
        </div>
        <div className="bg-white rounded-lg px-4 py-2.5 border border-gray-200">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Match Pts</div>
          <div className="text-lg font-bold text-blue-700">{matchResults.matchPointsTotal}</div>
        </div>
        <div className="bg-white rounded-lg px-4 py-2.5 border border-gray-200">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Total</div>
          <div className="text-lg font-bold text-gray-900">{teamPoints}</div>
        </div>
      </div>

      {/* Match list */}
      <div className="space-y-1.5">
        {matchResults.matches.map((match: MatchResult, i: number) => (
          <div
            key={i}
            className={`flex items-center gap-2 text-sm rounded-md px-3 py-1.5 ${
              match.won ? 'bg-green-50' : 'bg-red-50'
            }`}
          >
            <span
              className={`font-bold text-xs w-5 text-center ${
                match.won ? 'text-green-700' : 'text-red-600'
              }`}
            >
              {match.won ? 'W' : 'L'}
            </span>
            <span className="text-gray-500 text-xs w-28 shrink-0 truncate" title={match.round}>
              {match.round}
            </span>
            <span className="text-gray-700 text-xs truncate">
              {match.winType}
              {match.opponent && (
                <>
                  {' '}
                  vs {match.opponent}
                  {match.opponentTeam && (
                    <span className="text-gray-400"> ({match.opponentTeam})</span>
                  )}
                </>
              )}
            </span>
            {match.matchPoints > 0 && (
              <span className="ml-auto text-xs font-semibold text-blue-700 shrink-0">
                +{match.matchPoints}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Collapsible scoring legend */}
      <div className="mt-4 border-t border-gray-200 pt-3">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowLegend(!showLegend);
          }}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          <svg
            className={`w-3.5 h-3.5 transition-transform ${showLegend ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
          How does scoring work?
        </button>
        {showLegend && (
          <div className="mt-2 space-y-3 text-xs">
            <div>
              <div className="font-semibold text-gray-700 mb-1">Points per match</div>
              <div className="space-y-0.5">
                {SCORING_RULES.map((rule) => (
                  <div key={rule.label} className="flex gap-2">
                    <span className="text-gray-500 w-44 shrink-0">{rule.label}</span>
                    <span className="text-gray-700">{rule.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="font-semibold text-gray-700 mb-1">Win types</div>
              <div className="space-y-0.5">
                {WIN_TYPE_LEGEND.map((item) => (
                  <div key={item.type} className="flex gap-2">
                    <span className="text-gray-900 font-medium w-44 shrink-0">{item.type}</span>
                    <span className="text-gray-500">{item.description}</span>
                    <span className="ml-auto text-blue-600 font-medium shrink-0">{item.bonus}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function WrestlerTable({
  weightClass,
  wrestlers,
  hasResults,
}: {
  weightClass: string;
  wrestlers: Wrestler[];
  hasResults: boolean;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const seeded = wrestlers
    .filter((w) => w.seed !== null)
    .sort((a, b) => (a.seed ?? 99) - (b.seed ?? 99));
  const unseeded = wrestlers
    .filter((w) => w.seed === null)
    .sort((a, b) => a.name.localeCompare(b.name));
  const rows = [...seeded, ...unseeded];

  if (rows.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-5 py-3 bg-gray-50 border-b flex items-baseline gap-2">
        <h3 className="text-base font-semibold text-gray-900">{weightClass} lbs</h3>
        <span className="text-sm text-gray-500">{rows.length} wrestlers</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide">
              <th className="px-4 py-2 text-left w-16">Seed</th>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">School</th>
              {hasResults && (
                <>
                  <th className="px-4 py-2 text-left w-16">Place</th>
                  <th className="px-4 py-2 text-left w-20">Points</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((wrestler) => {
              const hasMatchData =
                wrestler.matchResults && wrestler.matchResults.matches.length > 0;
              const isExpanded = expandedId === wrestler.id;
              const colCount = hasResults ? 5 : 3;

              return (
                <React.Fragment key={wrestler.id}>
                  <tr
                    className={`${hasMatchData ? 'cursor-pointer' : ''} hover:bg-gray-50`}
                    onClick={() => hasMatchData && setExpandedId(isExpanded ? null : wrestler.id)}
                  >
                    <td className="px-4 py-2.5 text-gray-600 font-mono">
                      {wrestler.seed ? `#${wrestler.seed}` : '—'}
                    </td>
                    <td className="px-4 py-2.5 font-medium text-gray-900">
                      {wrestler.name}
                      {wrestler.year && (
                        <span className="ml-1.5 text-xs text-gray-400 font-normal">
                          {wrestler.year}
                        </span>
                      )}
                      {wrestler.previousPlacement && (
                        <span className="ml-1.5 text-xs text-purple-500 font-normal">
                          ({wrestler.previousPlacement})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">{wrestler.schoolTeam || '—'}</td>
                    {hasResults && (
                      <>
                        <td className="px-4 py-2.5 text-gray-900">
                          {wrestler.place ? ordinal(wrestler.place) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-gray-900">
                          <div className="flex items-center gap-1">
                            {wrestler.teamPoints > 0 ? wrestler.teamPoints : '—'}
                            {hasMatchData && (
                              <svg
                                className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                                />
                              </svg>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                  {isExpanded && wrestler.matchResults && (
                    <tr>
                      <td colSpan={colCount} className="p-0">
                        <ScoringBreakdown
                          matchResults={wrestler.matchResults}
                          teamPoints={wrestler.teamPoints}
                          place={wrestler.place}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export default function PublicTournamentPage() {
  const { id } = useParams<{ id: string }>();
  const [tournament, setTournament] = useState<TournamentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    tournamentApi
      .get(id)
      .then((data) => setTournament(data))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load tournament'))
      .finally(() => setLoading(false));
  }, [id]);

  const title = tournament?.name ?? 'Tournament';
  const description = tournament
    ? buildDescription(tournament)
    : 'View tournament seeds, brackets, and results on Takedown League.';

  useDocumentMeta(title, description);

  useStructuredData(
    tournament
      ? {
          '@context': 'https://schema.org',
          '@type': 'SportsEvent',
          name: tournament.name,
          description: buildDescription(tournament),
          sport: 'Wrestling',
          ...(tournament.startDate && { startDate: tournament.startDate }),
          ...(tournament.endDate && { endDate: tournament.endDate }),
          ...(tournament.location && {
            location: {
              '@type': 'Place',
              name: tournament.location,
            },
          }),
          organizer: {
            '@type': 'Organization',
            name: 'NCAA',
          },
          url: `https://takedownleague.com/tournaments/${tournament.id}`,
        }
      : {
          '@context': 'https://schema.org',
          '@type': 'SportsEvent',
          name: 'NCAA Wrestling Tournament',
          sport: 'Wrestling',
        }
  );

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
        </div>
      </PublicLayout>
    );
  }

  if (error || !tournament) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Tournament not found</h1>
          <p className="text-gray-600 mb-6">
            This tournament may have been removed or the link is incorrect.
          </p>
          <Link to="/" className="text-purple-600 hover:text-purple-800 font-medium">
            ← Back to home
          </Link>
        </div>
      </PublicLayout>
    );
  }

  const status = statusLabel(tournament.status);
  const hasResults = tournament.wrestlers.some(
    (w) =>
      w.place !== null || w.teamPoints > 0 || (w.matchResults && w.matchResults.matches.length > 0)
  );
  const isPreliminary = tournament.seedsArePreliminary;
  const hasSeeds = tournament.wrestlers.some((w) => w.seed !== null);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-600 via-purple-600 to-purple-700 text-white py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${status.className}`}>
              {status.text}
            </span>
          </div>
          <h1 className="text-4xl font-bold mb-4">{tournament.name}</h1>
          <div className="flex flex-wrap gap-5 text-purple-100 text-sm">
            {tournament.location && (
              <div className="flex items-center gap-1.5">
                <MapPinIcon className="w-4 h-4 flex-shrink-0" />
                <span>{tournament.location}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <CalendarIcon className="w-4 h-4 flex-shrink-0" />
              <span>
                {formatDateRange(tournament.startDate, tournament.endDate, { long: true })}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <ScaleIcon className="w-4 h-4 flex-shrink-0" />
              <span>{tournament.weightClasses.length} weight classes</span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-10 space-y-8">
        {/* External links */}
        {(tournament.floUrl || tournament.wrestlestatUrl) && (
          <div className="flex flex-wrap gap-3">
            {tournament.floUrl && (
              <a
                href={tournament.floUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-full text-white text-sm font-medium bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 shadow-sm transition-all"
              >
                Watch on FloWrestling
              </a>
            )}
            {tournament.wrestlestatUrl && (
              <a
                href={tournament.wrestlestatUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-full text-white text-sm font-medium bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-sm transition-all"
              >
                Wrestler Stats
              </a>
            )}
          </div>
        )}

        {/* Seeds status notice */}
        {isPreliminary ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-800">
            Seeds are preliminary and subject to change once official seeds are released.
          </div>
        ) : !hasSeeds ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-600">
            Seeds coming soon.
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800">
            Seeds are official.
          </div>
        )}

        {/* Description */}
        {tournament.description && (
          <p className="text-gray-700 text-base leading-relaxed">{tournament.description}</p>
        )}

        {/* Ad banner */}
        <AdBanner slot={AD_SLOTS.tournamentDetails} format="horizontal" />

        {/* Wrestler tables */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-5">
            {hasResults ? 'Results' : 'Field'} by Weight Class
          </h2>
          <div className="space-y-6">
            {tournament.weightClasses.map((wc) => {
              const wrestlers = tournament.wrestlersByWeight[wc] ?? [];
              return (
                <WrestlerTable
                  key={wc}
                  weightClass={wc}
                  wrestlers={wrestlers}
                  hasResults={hasResults}
                />
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 rounded-xl py-10 px-6 text-center text-white">
          <h2 className="text-2xl font-bold mb-2">Want to compete?</h2>
          <p className="text-purple-100 mb-6">
            Create a free Takedown League and draft these wrestlers with friends. Earn points based
            on real tournament results.
          </p>
          <Link
            to="/login?tab=register"
            className="inline-block bg-white text-purple-700 font-semibold py-3 px-8 rounded-lg hover:bg-purple-50 transition-colors text-lg"
          >
            Create a Free Account
          </Link>
        </section>
      </div>
    </PublicLayout>
  );
}

function buildDescription(t: TournamentDetails): string {
  const parts: string[] = [];
  const dateStr = formatDateRange(t.startDate, t.endDate, { long: true });
  if (t.location) {
    parts.push(`${t.name} at ${t.location}${dateStr !== 'TBD' ? `, ${dateStr}` : ''}.`);
  } else {
    parts.push(`${t.name}${dateStr !== 'TBD' ? `, ${dateStr}` : ''}.`);
  }
  if (t.weightClasses.length > 0) {
    parts.push(
      `View seeds and results across ${t.weightClasses.length} weight classes: ${t.weightClasses.join(', ')} lbs.`
    );
  }
  parts.push('Fantasy wrestling on Takedown League — free to play.');
  return parts.join(' ');
}
